/* eslint-disable react/no-unused-prop-types */
import React, { FocusEventHandler, FunctionComponent } from 'react';
import { Checkbox, DatePicker, Input, Select as SelectBR } from '@vwfs-bronson/bronson-react';
import { FormField } from '../../../components/FormField/FormField';
import { MaskedTextFormField, masks } from '../../../components/FormFields/MaskedTextFormField';
import { OptionsItem } from '../../../services/common/miscUtils.types';
import { addOnIcon } from '../utils/config';

export interface Props {
  setFieldValue?: any;
  values?: any;
  name?: any;
  label?: any;
  list?: any;
  addOn?: any;
  notRequired?: boolean;
  value?: any;
  infoText?: string;
  disabled?: any;
  reversed?: any;
  type?: any;
  minDate?: any;
  maxDate?: any;
  carTypeStep1?: any;
  placeholder?: any;
  dateFormatted?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  addOnText?: any;
  decimalForbidden?: boolean;
  onValueChange?: (newValue: string, numericValue?: number) => void;
}

export const InputField: FunctionComponent<Props> = ({
  name,
  label,
  addOn,
  notRequired,
  disabled,
  reversed,
  type,
  placeholder,
  value,
  onBlur,
}) => {
  return (
    <FormField
      name={name}
      type={type || 'input'}
      labelText={label}
      required={!notRequired}
      render={(fieldProps) => (
        <Input
          {...fieldProps}
          addonText={addOn || ''}
          disabled={disabled || false}
          reversed={reversed || false}
          placeholder={placeholder}
          onBlur={onBlur}
          value={value || ''}
          min="0"
        />
      )}
    />
  );
};

interface DropdownProps extends Props {
  list: OptionsItem[] | Readonly<OptionsItem[]>;
  /**
   * - If not passed then initial empty ("") value with label "Sélectionner" will be added.
   */
  noPlaceholder?: boolean;
  onSelect?: any;
}

export const Dropdown: FunctionComponent<DropdownProps> = ({
  name,
  label,
  list,
  notRequired,
  disabled,
  noPlaceholder,
  onSelect,
  onValueChange,
}) => {
  const listToRender = [noPlaceholder ? undefined : { label: 'Sélectionner', value: '' }, ...list].filter(
    Boolean
  ) as OptionsItem[];

  return (
    <FormField
      required={!notRequired}
      type="select"
      name={name}
      labelText={label}
      render={(fieldProps) => {
        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          fieldProps.onChange(e);
          if (onSelect) {
            onSelect(e);
          }
          const selectedValue = e.target.value;
          const selectedItem = listToRender.find((item) => item.value === selectedValue);
          const selectedLabel = selectedValue === '' ? '' : (selectedItem?.label ?? '');
          const removePriceOfSelectedValue = selectedLabel.split(' [')[0];

          // Extract numeric price from format like "[ 37 345,00 € ]"
          const priceMatch = selectedLabel.match(/\[\s*([\d\s,.-]+)\s*€\s*\]/);
          let numericPrice = 0;
          if (priceMatch) {
            // Remove spaces and replace comma with dot for parsing
            const cleanPrice = priceMatch[1].replace(/\s/g, '').replace(',', '.');
            numericPrice = parseFloat(cleanPrice) || 0;
          }
          onValueChange?.(removePriceOfSelectedValue, numericPrice);
        };

        return (
          <SelectBR {...fieldProps} disabled={disabled || false} onChange={handleChange}>
            {listToRender.map(({ label, value }) => (
              <SelectBR.Item key={value} value={value}>
                {label}
              </SelectBR.Item>
            ))}
          </SelectBR>
        );
      }}
    />
  );
};

export const Datepicker: FunctionComponent<Props> = ({
  name,
  label,
  notRequired,
  disabled,
  value,
  setFieldValue,
  maxDate,
  minDate,
  infoText,
  dateFormatted = 'd/m/Y',
  onValueChange,
}) => {
  return (
    <FormField
      name={name}
      type="other"
      labelText={label}
      required={!notRequired}
      infoText={infoText}
      render={(fieldProps) => (
        <DatePicker
          {...fieldProps}
          name={name}
          maxDate={maxDate}
          minDate={minDate}
          dateFormat={dateFormatted}
          locale="fr"
          placeholder="D/M/Y"
          mode="single"
          disabled={disabled || false}
          value={value}
          onChange={(e) => setFieldValue(name, e.split('/').join('/'))}
          onBlur={(e) => {
            setFieldValue(name, e.split('/').join('/'));
            onValueChange?.(e as string);
          }}
        />
      )}
    />
  );
};

export const CheckboxField: FunctionComponent<Props> = ({
  name,
  setFieldValue,
  value,
  label,
  disabled,
  onValueChange,
}) => (
  <FormField
    name={name}
    type="input"
    className="u-mt u-mt-none@s"
    required={false}
    render={() => (
      <Checkbox
        name={name}
        onChange={(e) => {
          setFieldValue(name, e.target.checked);
          onValueChange?.(`${e.target.checked}`);
        }}
        checked={value}
        disabled={disabled || false}
      >
        {label}
      </Checkbox>
    )}
  />
);

export const TotalPriceField: FunctionComponent<Props> = ({ name, label, value, addOnText = true, addOn }) => {
  return (
    <FormField
      name={name}
      type="input"
      labelText={label}
      className="is-readonly"
      render={() => (
        <Input
          id={`${name}-input`}
          modifierStates="is-readonly"
          readOnly
          {...(addOnText ? { addonText: addOn || addOnIcon.EURO } : {})}
          value={value || ''}
        />
      )}
    />
  );
};

export const LicensePlateInputField: FunctionComponent<Props> = ({ name, label, notRequired, disabled }) => (
  <MaskedTextFormField
    fieldName={name}
    optional={notRequired}
    labelTranslationKey={label}
    mask={masks.licensePlateMask}
    centerText
    isDisabled={disabled}
  />
);
